import { motion, useAnimation } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import { useTranslation } from 'react-i18next';

import { SectionWrapper } from '../hoc';
import { styles } from '../styles';
import { technologies } from '../data';
import { fadeIn, textVariant } from '../utils/motion';
import { ProfileIcon, SafeSvgRenderer } from './icons/AboutIcons.jsx';

const About = () => {
  const { t } = useTranslation();
  const controls = useAnimation();
  const { ref, inView } = useInView({ threshold: 0.2, triggerOnce: true });
  const [activeTech, setActiveTech] = useState(null);

  useEffect(() => {
    if (inView) {
      controls.start('show').then();
    }
  }, [controls, inView]);

  const handleTechTouch = (itemName) => {
    setActiveTech(itemName);
    setTimeout(() => {
      setActiveTech(null);
    }, 600);
  };

  return (
    <>
      <div className="pb-16 md:pb-20">
        <motion.div variants={textVariant()}>
          <h2 className={`${styles.sectionText} text-center mb-8 md:mb-14`}>
            {t('about.title')}
          </h2>
        </motion.div>

        <motion.div
          ref={ref}
          animate={controls}
          initial="hidden"
          variants={fadeIn('up', 'spring', 0.1, 0.8)}
          className="flex flex-col md:flex-row md:gap-24"
        >
          <motion.div className="w-full md:w-1/2 mb-8 md:mb-0">
            <div className="flex flex-col items-center w-full max-w-lg mx-auto">
              <ProfileIcon
                className="block w-1/2 h-auto mb-8 text-white"
                aria-label="Brain icon"
              />
              <p
                className={`${styles.paragraphText} text-center md:text-left mb-6`}
              >
                {t('about.p1')}
              </p>
              <p className={`${styles.paragraphText} text-center md:text-left`}>
                {t('about.p2')}
              </p>
            </div>
          </motion.div>

          <motion.div className="w-full md:w-1/2 md:flex md:flex-col md:items-center">
            <div className="space-y-8 w-full max-w-2xl">
              {technologies.map((techCategory) => (
                <div key={techCategory.category}>
                  <h3 className="text-white font-bold text-lg md:text-xl lg:text-2xl mb-3 uppercase tracking-wider">
                    {t(techCategory.category)}
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                    {techCategory.items.map((item) => {
                      const isActive = activeTech === item.name;
                      const iconSizeClass = 'w-8 h-8';
                      const textSizeAndWeight = `${styles.paragraphText} font-medium`;

                      const techItem = t(item.name, { returnObjects: true });

                      return (
                        <div
                          key={item.name}
                          className="flex items-center gap-2 p-4 rounded
                                                bg-tertiary bg-opacity-50
                                                hover:bg-opacity-75 transition-colors
                                                duration-200 cursor-default"
                          style={{
                            boxSizing: 'border-box',
                            userSelect: 'none',
                            outline: isActive ? `2px solid ${item.color}` : '',
                          }}
                          onTouchStart={() => handleTechTouch(item.name)}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.outline = `2px solid ${item.color}`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.outline = '';
                          }}
                        >
                          <SafeSvgRenderer
                            svgString={techItem.icon}
                            role="img"
                            className={`inline-block ${iconSizeClass} flex-shrink-0`}
                            aria-label={`${techItem.name} icon`}
                          />
                          <span
                            className={`${textSizeAndWeight} flex-1 text-center`}
                          >
                            {t(techItem.name)}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </motion.div>
      </div>
    </>
  );
};

export default SectionWrapper(About, 'about');
